import { NavStatusBadge, type NavStatusKind } from "@nu-assist/ui";
/**
 * DashboardGrid + DashboardCard + DashboardCardSkeleton.
 *
 * Reusable across other web screens. Grid shape:
 *   - base (`< md`):  1 column
 *   - `md` (≥768px):  2 columns, at every width above that
 *   - `besidePanel={false}`: 3 columns from `lg`, capped at 1,024px and
 *     centered (the shape before #853, for a home page with no panel)
 *
 * Two columns beside the panel (Trello #853, 2026-10-28). v35 `.home-grid` was
 * `repeat(3, 1fr)` capped at 960px and centered; on the home page that left
 * the "My open searches" panel beside it a fixed narrow column, so search
 * names truncated after a few words. Two columns of cards (three rows of
 * the six) hand that width to the panel. The 960px cap and centering went
 * with it: centered, the grid drifted away from the panel and opened an
 * empty gutter between them instead of giving the panel the space. Picked
 * over four other layouts compared on dev (2026-10-29).
 *
 * Card visual spec from v35 `.home-card`: surface bg, 1px border,
 * `rounded-lg` (12px ≈ v35's 16px — nearest token tier, picked by
 * intended px per the Phase-1 radius-rename trap note), generous
 * padding, centered column, accent border + lift on hover. The skeleton
 * variant renders shimmer blocks for the same frame (PRD: placeholder /
 * skeleton cards only — NO data, NO API, NO real widgets).
 *
 * Tokenized: only token utilities + Tailwind's 4px scale + `text-*`
 * roles. No inline styles, no arbitrary `text-[Npx]`.
 */
import type { ReactNode } from "react";

export interface DashboardGridProps {
  children: ReactNode;
  className?: string;
  /** False when nothing sits beside the grid: it then goes back to 3
   *  columns at `lg`, capped and centered, rather than two cards each half
   *  the page wide. */
  besidePanel?: boolean;
}

export function DashboardGrid({ children, className, besidePanel = true }: DashboardGridProps) {
  const grid = besidePanel
    ? "grid grid-cols-1 md:grid-cols-2 content-start gap-5"
    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto";

  return (
    <div data-testid="dashboard-grid" className={[grid, className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

export interface DashboardCardProps {
  /** Card title — the card's ONLY text (Trello #711 cl.2: icon + title, no
   *  helper line). The eyebrow and one-line description the v37 mock carried
   *  are gone, not hidden: keeping unrendered props invites them back. */
  title: string;
  /** Optional decorative icon block (v37 `.home-card-icon`). */
  icon?: ReactNode;
  /** Status pill, top-right (v37 `coming soon` cards). */
  status?: NavStatusKind;
  /** Inert in the scaffold; reserved for a future router. */
  onActivate?: () => void;
  /**
   * Renders the card in a greyed-out, non-interactive state — used by the
   * Dashboard when a section has NO released child page (so the dynamic
   * `firstReleasedPageHref` resolver returns `null`). Sets `aria-disabled`
   * + the native `disabled` attribute so assistive tech and the browser
   * both treat the card as inert, replacing the previous "dead click → /
   * dashboard bounce" failure mode with a legible "nothing's shipped
   * here yet" affordance. The visual styling stays in the card frame so
   * the lifecycle grid never goes blank.
   */
  disabled?: boolean;
}

export function DashboardCard({ title, icon, status, onActivate, disabled }: DashboardCardProps) {
  // Two visual states: the live, hover-reactive card (the original v35
  // treatment) and the disabled, muted card for sections with no
  // released child. The disabled state keeps the frame + content so the
  // operator sees the section is on the roadmap, just not yet
  // navigable.
  const liveCard =
    "group relative flex flex-col items-center text-center gap-2 p-6 bg-surface border border-border rounded-lg cursor-pointer outline-none transition-all hover:border-border-hover hover:-translate-y-0.5 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-accent-muted";
  const disabledCard =
    "relative flex flex-col items-center text-center gap-2 p-6 bg-surface border border-border rounded-lg outline-none opacity-50 cursor-not-allowed";
  const card = disabled ? disabledCard : liveCard;

  return (
    <button
      type="button"
      data-testid="dashboard-card"
      onClick={disabled ? undefined : onActivate}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className={card}
    >
      {status ? <NavStatusBadge kind={status} className="absolute top-4 right-4" /> : null}
      {icon ? (
        <div
          aria-hidden="true"
          className="flex items-center justify-center w-14 h-14 mb-1 rounded-lg bg-accent-muted text-accent transition-colors group-hover:bg-accent group-hover:text-bg"
        >
          {icon}
        </div>
      ) : null}
      <h3 className="text-h2 text-text">{title}</h3>
    </button>
  );
}

/**
 * DashboardCardSkeleton — same frame, shimmer placeholders. Used to
 * scaffold loading/empty states without any data dependency.
 */
export function DashboardCardSkeleton() {
  const card =
    "flex flex-col items-center text-center gap-3 p-6 bg-surface border border-border rounded-lg";
  const block = "animate-pulse rounded-md bg-border";

  return (
    <div data-testid="dashboard-card-skeleton" aria-hidden="true" className={card}>
      <div className={`${block} w-14 h-14 rounded-lg`} />
      <div className={`${block} w-40 h-4`} />
    </div>
  );
}
